import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { Users } from 'app/modal/users';
import { ApiService } from 'app/services/api.service';
import { catchError, Observable, of, ReplaySubject, switchMap, tap, throwError } from 'rxjs';
import { MessagingService } from 'app/services/messaging.service';

@Injectable({ providedIn: 'root' })
export class AuthService {

    private context = "/api";

    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private _apiService = inject(ApiService);
    private _messagingService = inject(MessagingService);

    private _user: ReplaySubject<Users> = new ReplaySubject<Users>(1);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }


    set user(value: Users) {
        this._user.next(value);
    }

    get user$(): Observable<Users> {
        return this._user.asObservable();
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }
        let url = '/admin/auth/sign-in?username=' + encodeURIComponent(credentials.email) + '&password=' + encodeURIComponent(credentials.password);

        return this._httpClient.post(url, {
            "Content-Type": "application/json"
        }).pipe(
            switchMap((response: any) => {
                if (!response.errorMsg) {
                    this.onLogin(response);
                }
                // Return a new observable with the response
                return of(response);
            })
        );
    }

    public initialDataFetched: boolean = false;

    onLogin(response: any) {
        this.initialDataFetched = false;
        this.updateUsersResponse(response.user);
        // Store the access token in the local storage
        this.authToken = response.accessToken;
        this.accessToken = response.accessToken;
        this._setSession(response.user);

        // Store the user on the user service
        this._userService.user = response.user
        this.user = response.user;

        // Register FCM
        if (response.user && response.user.id) {
            this._messagingService.requestPermission()
            this._messagingService.register(String(response.user.id));
            this._messagingService.receiveMessage();
        }

        response.user;
    }

    updateUsersResponse(user: any) {
        //user.name = user.firstname ;
    }

    public _setSession(profile: Users) {
        this.userProfile = profile;
        // Set the authenticated flag to true
        this._authenticated = true;
        localStorage.setItem("loggedIn-user-data", JSON.stringify(profile));
        localStorage.setItem("user-logged-in", "true");
    }

    set userProfile(user: Users) {
        localStorage.setItem("loggedIn-user-data", JSON.stringify(user));
    }

    get userProfile(): Users {
        let token = localStorage.getItem("loggedIn-user-data");
        if (token) {
            return JSON.parse(token);
        }
        return new Users();
    }

    /**
     * Setter & getter for access token
     */
    set authToken(token: string) {
        /* if(token == "undefined"){
            localStorage.removeItem('access-token')
        } */
        localStorage.setItem('access-token', token);
    }

    get authToken(): string {
        return localStorage.getItem('access-token') ?? '';
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token

        return this._httpClient.post('/admin/auth/refresh-access-token', {
            "Content-Type": "application/json",
            accessToken: this.accessToken,
        }).pipe(
            catchError(() =>
                // Return false
                of(false),
            ),
            switchMap((response: any) => {
                if (response == false) {
                    return of(false);
                } else {
                    this.onLogin(response);
                    // Return true
                    return of(true);
                }
            }),
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Deregister FCM
        const userId = this.userProfile?.id;
        if (userId) {
            this._messagingService.deregister(String(userId));
        }

        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Reset the initial data flag
        this.initialDataFetched = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: any): Observable<any> {
        return this._httpClient.post('api/admin/unsecure/Users/signup', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }


    validatePermission(permissions: string[]) {
        /*let user = this.userProfile;
        let activeRole = user.activeRole;
        if (activeRole.rolename == Constants.ROLE_SUPER_ADMIN) {
            return true;
        } else {
            let allowedPermissions = activeRole.permissions.filter(permission => permissions.includes(permission.permissionname));
            if (allowedPermissions.length > 0) {
                return true;
            } else {
                return false;
            }
        }*/
    }

    getUserInContext() {
        return this._apiService.sendGETRequest('/v1/Users/getUserInContext').pipe(
            tap((response: Users) => {
                this._setSession(response);
                // Store the user on the user service
                this._userService.user = response;
                this.user = response;
            }),
        );
    }
}
